import type { SchemaInput } from './schema-input.js';
import { isString } from './runtime-values.js';
export interface GeocoderPresentationInput {
  name?: unknown;
  street?: unknown;
  housenumber?: unknown;
  city?: unknown;
  town?: unknown;
  village?: unknown;
  municipality?: unknown;
  district?: unknown;
  county?: unknown;
  state?: unknown;
  postcode?: unknown;
  country?: unknown;
  fallbackLabel?: unknown;
}

export interface GeocoderPresentation {
  formatted: string;
  primary: string;
  secondary?: string;
}

function text(value: SchemaInput): string | undefined {
  return isString(value) && value.trim().length > 0 ? value.trim() : undefined;
}

function comparisonKey(value: string): string {
  return value.normalize('NFKD').replace(/\p{Mark}/gu, '').toLowerCase().replace(/[^\p{Letter}\p{Number}]+/gu, ' ').trim();
}

function addressLine(input: GeocoderPresentationInput): string | undefined {
  const street = text(input.street);
  const housenumber = text(input.housenumber);
  if (!street) return undefined;
  return housenumber ? `${street} ${housenumber}` : street;
}

export function buildGeocoderPresentation(input: GeocoderPresentationInput): GeocoderPresentation | null {
  const fallback = text(input.fallbackLabel);
  const fallbackParts = fallback?.split(',').map((part) => part.trim()).filter(Boolean) ?? [];
  const address = addressLine(input);
  const primary = text(input.name) || address || fallbackParts[0];
  if (!primary) return null;
  const seen = new Set([comparisonKey(primary)]);
  const secondaryParts: string[] = [];
  const add = (value: string | undefined) => {
    if (!value) return false;
    const key = comparisonKey(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    secondaryParts.push(value);
    return true;
  };
  if (text(input.name)) add(address);
  const locality = [input.city, input.town, input.village, input.municipality].map(text).find(Boolean);
  const postcode = text(input.postcode);
  const localityIsNew = locality && !seen.has(comparisonKey(locality));
  if (localityIsNew && postcode) add(`${postcode} ${locality}`);
  else {
    if (localityIsNew) add(locality);
    add(postcode);
  }
  if (!localityIsNew) add([input.district, input.county].map(text).find((value) => value && !seen.has(comparisonKey(value))));
  add(text(input.state));
  add(text(input.country));
  if (secondaryParts.length === 0 && fallbackParts.length > 1) for (const part of fallbackParts.slice(1)) add(part);
  const secondary = secondaryParts.length > 0 ? secondaryParts.join(' · ') : undefined;
  return { primary, secondary, formatted: [primary, ...secondaryParts].join(', ') };
}
